import { axisBottom, axisLeft } from "d3-axis";
import { bin, max, range, sum } from "d3-array";
import React, { useEffect, useMemo } from "react";
import { scaleLinear, scaleTime } from "d3-scale";
import {
  placePhotosWithLocationState,
  userSelectionBboxState,
} from "./TrendingMap";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { localPoint } from "@visx/event";
import { useDrag } from "@use-gesture/react";
import { checkedCategoriesState } from "./CategoryControl";
import { syncEffect } from "recoil-sync";
import { jsonDate } from "@recoiljs/refine";

export const timeSelectionState = atom({
  key: "timeSelectionState",
  default: new Date(),
  effects: [
    syncEffect({
      refine: jsonDate(),
    }),
  ],
});

const HistogramBins = ({ bins, xScale, yScale, Y, height }) => {
  return bins.map((bin, i) => (
    <rect
      key={i}
      x={xScale(bin.x0)}
      width={xScale(bin.x1) - xScale(bin.x0)}
      y={height - yScale(Y[i])}
      height={yScale(Y[i])}
      fill="#D19DD0"
      opacity="0.85"
      style={{ pointerEvents: "none" }}
    />
  ));
};

const PhotoHistogram = ({ height = 300, width = 1000 }) => {
  const userSelectionBbox = useRecoilValue(userSelectionBboxState);
  const [timeSelection, setTimeSelection] = useRecoilState(timeSelectionState);
  const categories = useRecoilValue(checkedCategoriesState);
  const categoriesString = categories.join(",");

  const placePhotos = useRecoilValue(
    placePhotosWithLocationState({
      ...userSelectionBbox,
      categories: categoriesString,
    })
  );

  const { bins, yAxis, yFormat, yScale, xScale, Y } = useMemo(() => {
    const thresholds = 180;
    const photosWithDate = placePhotos;
    // Compute values.
    const X = photosWithDate.map((d) => d.date);
    const Y0 = photosWithDate.map((d) => 1);
    const I = range(X.length);

    // Compute bins.
    const bins = bin()
      .thresholds(thresholds)
      .value((i) => X[i])(I);
    const Y = Array.from(bins, (I) => sum(I, (i) => Y0[i]));

    // Compute default domains.
    const xDomain = [bins[0].x0, bins[bins.length - 1].x1];
    const yDomain = [0, max(Y)];

    // Construct scales and axes.
    const xScale = scaleTime().domain(xDomain).range([0, width]);
    const yScale = scaleLinear().domain(yDomain).range([0, height]);
    const xAxis = axisBottom(xScale)
      .ticks(width / 80, "")
      .tickSizeOuter(0);
    const yAxis = axisLeft(yScale).ticks(height / 40, "");
    const yFormat = yScale.tickFormat(100, "");
    return { bins, yAxis, yFormat, yScale, xScale, Y };
  }, [height, placePhotos, width]);
  useEffect(() => {
    if (!timeSelection) {
      setTimeSelection(xScale.domain()[0]);
    }
  }, [xScale, setTimeSelection]);
  const bind = useDrag(({ event }) => {
    const { x } = localPoint(event);
    setTimeSelection(xScale.invert(x));
  });

  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% - 150px)",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <svg style={{ pointerEvents: "all" }} height={height} width={width}>
        <rect
          onClick={(e) => {
            const { x } = localPoint(e);
            setTimeSelection(xScale.invert(x));
          }}
          height="100%"
          width="100%"
          fill="grey"
          opacity="0.15"
        />
        <HistogramBins
          bins={bins}
          xScale={xScale}
          yScale={yScale}
          Y={Y}
          height={height}
        />
        <line
          style={{ pointerEvents: "all" }}
          y1="0%"
          y2="100%"
          opacity={0.6}
          x1={xScale(timeSelection)}
          strokeWidth="6"
          x2={xScale(timeSelection)}
          {...bind()}
          stroke="#9DD19F"
        />
      </svg>
    </div>
  );
};

export default PhotoHistogram;
